/**
 * <NeoPayBadge id="..." type="coin_transfer" />
 *
 * iter197 — Tiny inline pill that shows on-chain status of a SplitEase
 * primitive (coin transfer, fund_reimburse, expense, fund_transaction).
 * Clicking it navigates to the FULL Authenticity Certificate page.
 *
 * iter198 — Extended beyond coin_transfer rows. Now also lights up next to
 * approved expense rows and any fund_transaction (fine / contribution /
 * session_fee / coin_allot / loan / repay…) that has a NeoPay tx stamp.
 *
 * Eligibility map (controls which API endpoint + cert URL the badge uses):
 *   type=coin_transfer   → /neopay/transfers/{id}/proof   → /proof/{id}
 *   type=fund_reimburse  → /neopay/transfers/{id}/proof   → /proof/{id}
 *   type=expense         → /neopay/expenses/{id}/proof    → /proof/expense/{id}
 *   type=fund_transaction→ /neopay/fund-txns/{id}/proof   → /proof/fund-txn/{id}
 *
 * Backwards compatible — old call sites that pass `transferId` keep working.
 */
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import api from "../lib/api";
import { CheckCircle2, Clock, ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";

let _cachedEnabled = null; // module-level cache so we hit /status once

async function _isEnabled() {
  if (_cachedEnabled !== null) return _cachedEnabled;
  try {
    const r = await api.get("/neopay/status");
    _cachedEnabled = !!r.data?.enabled;
  } catch {
    _cachedEnabled = false;
  }
  return _cachedEnabled;
}

const PILL_BASE =
  "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs " +
  "uppercase tracking-wider font-medium border cursor-pointer select-none " +
  "transition-colors no-underline";

// Map each supported type to its proof-fetch endpoint, replay endpoint and
// cert path. Anything not in this map silently disables the badge.
const TYPE_CONFIG = {
  coin_transfer: {
    proofPath: (id) => `/neopay/transfers/${id}/proof`,
    replayPath: (id) => `/neopay/transfers/${id}/replay`,
    certPath: (id) => `/proof/${id}`,
  },
  fund_reimburse: {
    proofPath: (id) => `/neopay/transfers/${id}/proof`,
    replayPath: (id) => `/neopay/transfers/${id}/replay`,
    certPath: (id) => `/proof/${id}`,
  },
  expense: {
    proofPath: (id) => `/neopay/expenses/${id}/proof`,
    replayPath: (id) => `/neopay/expenses/${id}/replay`,
    certPath: (id) => `/proof/expense/${id}`,
  },
  fund_transaction: {
    proofPath: (id) => `/neopay/fund-txns/${id}/proof`,
    replayPath: (id) => `/neopay/fund-txns/${id}/replay`,
    certPath: (id) => `/proof/fund-txn/${id}`,
  },
};

export default function NeoPayBadge({ id, transferId, type, isAdmin = false }) {
  // Back-compat: legacy call sites pass `transferId` instead of `id`.
  const effectiveId = id || transferId;
  const [enabled, setEnabled] = useState(null);
  const [state, setState] = useState({ status: "loading", error: null });

  const cfg = useMemo(() => TYPE_CONFIG[type] || null, [type]);
  const eligible = useMemo(
    () => Boolean(effectiveId) && Boolean(cfg),
    [effectiveId, cfg]
  );

  useEffect(() => {
    let alive = true;
    if (!eligible) return;
    (async () => {
      const en = await _isEnabled();
      if (!alive) return;
      setEnabled(en);
      if (!en) return;
      try {
        const r = await api.get(cfg.proofPath(effectiveId));
        if (!alive) return;
        setState({
          status: r.data?.neopay_status || "settled",
          error: null,
        });
      } catch (e) {
        if (!alive) return;
        const code = e.response?.data?.detail?.code || e.response?.status;
        if (code === "NOT_MIRRORED" || code === 404) {
          setState({ status: "not_mirrored", error: null });
        } else if (code === "INTEGRATION_DISABLED" || code === 503) {
          setEnabled(false);
        } else {
          setState({ status: "error", error: code });
        }
      }
    })();
    return () => { alive = false; };
  }, [eligible, effectiveId, cfg]);

  if (!eligible || enabled === false) return null;
  if (enabled === null && state.status === "loading") {
    return (
      <span className={`${PILL_BASE} border-muted text-muted-foreground bg-muted/30`} data-testid={`neopay-badge-loading-${effectiveId}`}>
        <Loader2 className="w-2.5 h-2.5 animate-spin" /> ledger
      </span>
    );
  }

  const onReplay = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const r = await api.post(cfg.replayPath(effectiveId));
      toast.success(`Replay → ${r.data?.neopay_status || "submitted"}`);
      try {
        const r2 = await api.get(cfg.proofPath(effectiveId));
        setState({ status: r2.data?.neopay_status || "settled", error: null });
      } catch {
        setState({ status: "pending", error: null });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail?.message
        || err.response?.data?.detail
        || "Replay failed");
    }
  };

  const certPath = cfg.certPath(effectiveId);
  const stopProp = (e) => e.stopPropagation();

  switch (state.status) {
    case "settled":
      return (
        <RouterLink
          to={certPath}
          onClick={stopProp}
          className={`${PILL_BASE} border-emerald-500/40 text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20`}
          data-testid={`neopay-badge-${effectiveId}`}
          title="View Authenticity Certificate"
        >
          <CheckCircle2 className="w-2.5 h-2.5" /> ledger ✓
        </RouterLink>
      );
    case "pending":
      return (
        <RouterLink
          to={certPath}
          onClick={stopProp}
          className={`${PILL_BASE} border-muted-foreground/40 text-muted-foreground bg-muted/30 hover:bg-muted/50`}
          data-testid={`neopay-badge-${effectiveId}`}
          title="Settling on chain — view certificate"
        >
          <Clock className="w-2.5 h-2.5" /> verifying…
        </RouterLink>
      );
    case "held":
    case "rejected":
    case "submit_failed":
    case "not_mirrored":
      return (
        <RouterLink
          to={certPath}
          onClick={isAdmin ? onReplay : stopProp}
          className={`${PILL_BASE} border-amber-500/40 text-amber-600 dark:text-amber-400 bg-amber-500/10 hover:bg-amber-500/20`}
          data-testid={`neopay-badge-${effectiveId}`}
          title={isAdmin ? "Click to retry mirror" : "Not yet on chain — tap for details"}
        >
          <ShieldAlert className="w-2.5 h-2.5" /> {state.status === "not_mirrored" ? "no ledger" : state.status}
        </RouterLink>
      );
    default:
      return null;
  }
}
